import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TIPO_ACAO_OPTIONS } from "../../../lib/energia/melhoria-types";
import { AcaoForm } from "./melhoria-form-state";
import { DeptOrTextInput } from "./dept-or-text-input";

// ---------------------------------------------------------------------------
// AcaoRow sub-component
// ---------------------------------------------------------------------------

export function AcaoRow({
  index,
  acao,
  onChange,
  onRemove,
  canRemove,
}: {
  index: number;
  acao: AcaoForm;
  onChange: (key: keyof AcaoForm, value: string) => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  return (
    <div className="rounded-lg border p-3 space-y-3 bg-muted/30">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Ação {index + 1}
        </span>
        {canRemove && (
          <Button aria-label="Eliminar melhoria" size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={onRemove}>
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <div className="space-y-1">
          <Label className="text-xs">Tipo de ação</Label>
          <Select value={acao.tipo_acao} onValueChange={(v) => onChange("tipo_acao", v)}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Selecionar..." />
            </SelectTrigger>
            <SelectContent>
              {TIPO_ACAO_OPTIONS.map((opt) => (
                <SelectItem key={opt} value={opt} className="text-xs">
                  {opt === "Ação Corretiva" ? "AC — Ação Corretiva" :
                   opt === "Ação de Correção" ? "CC — Ação de Correção" :
                   "AM — Ação de Melhoria"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Prazo previsto</Label>
          <Input
            className="h-8 text-xs"
            value={acao.prazo_previsto}
            onChange={(e) => onChange("prazo_previsto", e.target.value)}
            placeholder="Ex: 2025-06-30 ou Imediato"
          />
        </div>
        <DeptOrTextInput
          label="Responsável"
          textValue={acao.responsavel}
          deptId={acao.responsavel_dept_id}
          onTextChange={(v) => onChange("responsavel", v)}
          onDeptChange={(id, code) => {
            onChange("responsavel_dept_id", id);
            if (code) onChange("responsavel", code);
          }}
          placeholder="Ex: DMC"
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs">Ação a Implementar</Label>
        <Textarea
          className="text-xs resize-none"
          rows={2}
          value={acao.acao_implementar}
          onChange={(e) => onChange("acao_implementar", e.target.value)}
          placeholder="Descreve a ação a implementar..."
        />
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <div className="space-y-1 sm:col-span-2">
          <Label className="text-xs">Ação Implementada</Label>
          <Textarea
            className="text-xs resize-none"
            rows={2}
            value={acao.acao_implementada}
            onChange={(e) => onChange("acao_implementada", e.target.value)}
            placeholder="Resultado / o que foi feito..."
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Data implementação</Label>
          <Input
            type="date"
            className="h-8 text-xs"
            value={acao.data_implementacao}
            onChange={(e) => onChange("data_implementacao", e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}
